import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Shield, Eye, Plus, Pencil, Trash2, FileText, Clock } from "lucide-react";

const ACTION_ICONS = {
  VIEW: <Eye className="h-4 w-4 text-blue-500" />,
  CREATE: <Plus className="h-4 w-4 text-green-500" />,
  UPDATE: <Pencil className="h-4 w-4 text-amber-500" />,
  DELETE: <Trash2 className="h-4 w-4 text-red-500" />,
};

const RESOURCE_LABELS = {
  children: "Children Profile",
  providers: "Therapist",
  carers: "Carer",
  appointments: "Appointment",
  "session-notes": "Session Note",
  goals: "Goal",
  activities: "Activity",
  documents: "Document",
};

export default function AuditLogPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const LIMIT = 25;

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const params = { limit: LIMIT, offset: page * LIMIT };
        if (filter) params.resource_type = filter;
        const response = await apiClient.get("/audit-logs", { params });
        setLogs(response.data.logs);
        setTotal(response.data.total);
      } catch (err) {
        console.error("Failed to fetch audit logs:", err);
      }
      setLoading(false);
    };
    fetchLogs();
  }, [token, filter, page]);

  const formatDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
      " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">Audit Log</h1>
        <p className="text-muted-foreground mt-1">Track all access to protected health information (PHI)</p>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">PHI Access History</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                className="px-3 py-2 rounded-lg border text-sm bg-white"
                data-testid="audit-filter"
              >
                <option value="">All Resources</option>
                {Object.entries(RESOURCE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">{total} entries</span>
            </div>
          </div>
          <CardDescription>
            HIPAA-compliant record of who accessed, created, updated, or deleted PHI data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10 text-muted-foreground">Loading audit logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-10">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No audit entries yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">Access to PHI will be logged here automatically</p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="hidden sm:grid grid-cols-12 gap-3 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b">
                <div className="col-span-2">Time</div>
                <div className="col-span-2">Action</div>
                <div className="col-span-3">Resource</div>
                <div className="col-span-3">IP Address</div>
                <div className="col-span-2">Status</div>
              </div>
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-3 px-3 py-3 rounded-lg hover:bg-muted/50 text-sm border-b last:border-0"
                  data-testid={`audit-entry-${log.id}`}
                >
                  <div className="col-span-2 flex items-center gap-1.5 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 hidden sm:block" />
                    <span className="text-xs">{formatDate(log.timestamp)}</span>
                  </div>
                  <div className="col-span-2 flex items-center gap-1.5">
                    {ACTION_ICONS[log.action] || null}
                    <span className="font-medium">{log.action}</span>
                  </div>
                  <div className="col-span-3">
                    <span className="text-foreground">{RESOURCE_LABELS[log.resource_type] || log.resource_type}</span>
                    {log.resource_id && (
                      <span className="text-xs text-muted-foreground ml-1 font-mono">#{log.resource_id.slice(0, 8)}</span>
                    )}
                  </div>
                  <div className="col-span-3 text-muted-foreground font-mono text-xs flex items-center">
                    {log.ip_address}
                  </div>
                  <div className="col-span-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      {log.status_code}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {total > LIMIT && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-4 py-2 text-sm rounded-lg border disabled:opacity-40 hover:bg-muted"
                data-testid="audit-prev-page"
              >
                Previous
              </button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {Math.ceil(total / LIMIT)}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * LIMIT >= total}
                className="px-4 py-2 text-sm rounded-lg border disabled:opacity-40 hover:bg-muted"
                data-testid="audit-next-page"
              >
                Next
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
